import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "@/lib/useInView";

export function HeroSnow() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext?.("2d");
    if (!ctx) return undefined;
    const still = prefersReducedMotion();
    let w = 0, h = 0, flakes = [], raf = 0, inView = true, boost = 0;
    let lastY = window.scrollY, lastT = performance.now();

    const make = (anywhere) => {
      const r = 0.8 + Math.random() ** 1.6 * 2.6;
      return {
        x: Math.random() * w, y: anywhere ? Math.random() * h : -6, r,
        s: 0.1 + r * 0.07 + Math.random() * 0.12, d: Math.random() * 6.28,
        sw: 0.08 + Math.random() * 0.32, f: 1400 + Math.random() * 2600, a: 0.55 + Math.random() * 0.4,
      };
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "rgba(36,69,61,0.16)";
      ctx.lineWidth = 0.6;
      for (const f of flakes) {
        if (!still) {
          f.y += f.s * (1 + boost);
          f.x += Math.sin(t / f.f + f.d) * f.sw;
          if (f.y > h + 6) Object.assign(f, make(false));
        }
        ctx.globalAlpha = f.a;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, 6.283);
        ctx.fill();
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };
    const frame = (t) => {
      boost *= 0.95;
      draw(t);
      window.__ofSnowFrames = (window.__ofSnowFrames || 0) + 1;
      raf = requestAnimationFrame(frame);
    };
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = rect.width; h = rect.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      flakes = Array.from({ length: w < 640 ? 30 : 70 }, () => make(true));
      if (still) draw(0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);
    if (still) return () => ro.disconnect();

    const sync = () => {
      const run = inView && !document.hidden;
      if (run && !raf) raf = requestAnimationFrame(frame);
      if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
    };
    const onScroll = () => {
      const now = performance.now();
      const v = Math.abs(window.scrollY - lastY) / Math.max(16, now - lastT);
      boost = Math.min(boost + v * 1.2, 1.5);
      lastY = window.scrollY; lastT = now;
    };
    const io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; sync(); });
    io.observe(canvas.parentElement);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("scroll", onScroll, { passive: true });
    sync();
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect(); ro.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return <canvas ref={ref} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" data-testid="hero-snow" />;
}
